import { EventsService } from "@/api/events";
import type {
  EventCreate,
  EventUpdate,
  StaffMember,
  EventAthlete,
  EventAttendance,
  UpdateMode,
} from "@/api/events/types";
import type {
  EventModel,
  EventCreationFormData,
  VacancyCreationFormData,
} from "@/schemas/event";
import { EVENTABLE_TYPE } from "@/constants/events";
import { Event } from "@/api/events/types";
import { isGroupSessionDetail } from "@/utils/events";
import { buildZonedDateTime } from "@/utils/time-helpers";
import { normalizeForSearch } from "@/utils/normalize-search";

function isWrappedStaffMember(
  value: unknown,
): value is { staffMember: StaffMember } {
  return typeof value === "object" && value !== null && "staffMember" in value;
}

function extractAthlete(event: Event): EventAthlete | null {
  if (!event.eventable || !("athlete" in event.eventable)) return null;
  return event.eventable.athlete ?? null;
}

function extractStaffMember(event: Event): StaffMember | null {
  if (!event.eventable) return null;

  if ("staffMember" in event.eventable) {
    const eventableStaffMember = event.eventable.staffMember;

    if (!eventableStaffMember) return null;

    if (isWrappedStaffMember(eventableStaffMember)) {
      return eventableStaffMember.staffMember;
    }
    return eventableStaffMember;
  }

  return null;
}

export class EventsRepository {
  private static _instance: EventsRepository;

  static get instance(): EventsRepository {
    if (!this._instance) {
      this._instance = new EventsRepository();
    }
    return this._instance;
  }

  private constructor() {}

  // =============================
  // READ
  // =============================

  async getEventDetail(gymId: number, eventId: number): Promise<EventModel> {
    const event = await EventsService.instance.getEvent(gymId, eventId);
    const staff = extractStaffMember(event);
    const athlete = extractAthlete(event);

    const base: EventModel = {
      id: event.id,
      gymId: event.gym.id,

      title: event.title,
      description: event.description ?? null,
      startsAt: event.startsAt,
      endsAt: event.endsAt,

      recurrenceRules:
        event.recurrenceRules?.frequency && event.recurrenceRules?.until
          ? {
              frequency: event.recurrenceRules.frequency,
              until: event.recurrenceRules.until,
            }
          : null,
      eventableType: event.eventableType,

      recommendedCapacity: event.recommendedCapacity ?? null,
      attendancesCount: event.attendancesCount ?? 0,
      gymScheduleStatus: event.gymSchedule?.status ?? null,
      gymScheduleStartsAt: event.gymSchedule?.startsAt ?? null,
      gymScheduleEndsAt: event.gymSchedule?.endsAt ?? null,

      staffMemberId: staff?.id ?? null,
      staffMemberName: staff?.name ?? null,
      staffMemberAvatarUrl: staff?.avatarUrl ?? null,

      athleteId: athlete?.id ?? null,
      athleteName: athlete?.name ?? null,

      difficultyLevel: null,
      disciplineId: null,
      disciplineName: null,
      disciplineAbbreviation: null,
      disciplineColor: null,

      roomId: null,
      roomName: null,
      roomCapacity: null,

      bodyFatPercentage:
        event.eventableType === EVENTABLE_TYPE.PhysicalEvaluationSession
          ? event.eventable.bodyFatPercentage
          : null,
      nextEval:
        event.eventableType === EVENTABLE_TYPE.PhysicalEvaluationSession
          ? event.eventable.nextEval
          : null,

      reason:
        event.eventableType === EVENTABLE_TYPE.Vacancy
          ? event.eventable.reason
          : null,

      isAnchorEvent: event.anchor,
    };

    if (isGroupSessionDetail(event)) {
      const dr = event.eventable.disciplineRoom;

      const discipline = dr?.discipline ?? null;
      const room = dr?.gymRoom ?? null;

      return {
        ...base,
        difficultyLevel: event.eventable.difficultyLevel ?? null,

        disciplineId: discipline?.id != null ? Number(discipline.id) : null,
        disciplineName: discipline?.name ?? null,
        disciplineAbbreviation: discipline?.abbreviation ?? null,
        disciplineColor: discipline?.color ?? null,

        roomId: room?.id != null ? Number(room.id) : null,
        roomName: room?.name ?? null,
        roomCapacity: room?.maximumCapacity ?? null,

        recommendedCapacity:
          event.recommendedCapacity ?? dr?.recommendedCapacity ?? null,
      };
    }

    return base;
  }

  // =============================
  // CREATE
  // =============================

  buildCreatePayload(
    model: EventCreationFormData,
    timezone: string,
  ): EventCreate {
    const buildDateTime = (day: string, time: string): string =>
      buildZonedDateTime(day, time, timezone);

    const base: EventCreate = {
      eventableType: model.eventableType,
      title: model.title,
      description: model.description ?? "",
      recommendedCapacity: model.recommendedCapacity ?? null,
      startsAt: buildDateTime(model.day, model.startTime),
      endsAt: buildDateTime(model.day, model.endTime),
      gymScheduleId: model.gymScheduleId ?? null,
      parentEventId: null,
      athleteId: model.athleteId ?? null,
      groupSession: null,
      physicalEvaluationSession: null,
      personalTrainingSession: null,
      floorDuty: null,
      recurrenceRules: model.recurrenceRules ?? null,
    };

    switch (model.eventableType) {
      case EVENTABLE_TYPE.GroupSession:
        if (
          !model.staffMemberId ||
          !model.disciplineId ||
          !model.roomId ||
          !model.difficultyLevel
        )
          return base;
        return {
          ...base,
          anchor: model.isAnchorEvent ?? false,
          groupSession: {
            staffMemberId: model.staffMemberId,
            disciplineId: model.disciplineId,
            gymRoomId: model.roomId,
            difficultyLevel: model.difficultyLevel,
          },
        };

      case EVENTABLE_TYPE.FloorDuty:
        if (!model.staffMemberId) return base;
        return { ...base, floorDuty: { staffMemberId: model.staffMemberId } };

      case EVENTABLE_TYPE.PersonalTrainingSession:
        if (!model.staffMemberId) return base;
        return {
          ...base,
          personalTrainingSession: { staffMemberId: model.staffMemberId },
        };

      case EVENTABLE_TYPE.PhysicalEvaluationSession:
        if (!model.staffMemberId) return base;
        return {
          ...base,
          physicalEvaluationSession: {
            staffMemberId: model.staffMemberId,
            ...(model.bodyFatPercentage != null && {
              bodyFatPercentage: model.bodyFatPercentage,
            }),
            ...(model.nextEval && { nextEval: model.nextEval }),
          },
        };

      default:
        return base;
    }
  }

  async createEvent(
    gymId: number,
    form: EventCreationFormData,
    timezone: string,
  ): Promise<Event> {
    const payload = this.buildCreatePayload(form, timezone);
    return EventsService.instance.createEvent(gymId, payload);
  }

  // =============================
  // FORM HELPERS
  // =============================

  buildUpdatePayload(
    model: EventCreationFormData,
    timezone: string,
  ): EventUpdate {
    const buildDateTime = (day: string, time: string): string =>
      buildZonedDateTime(day, time, timezone);

    const base: EventUpdate = {
      title: model.title,
      description: model.description ?? "",
      recommendedCapacity:
        model.recommendedCapacity === null ||
        model.recommendedCapacity === undefined
          ? null
          : Number(model.recommendedCapacity),
      startsAt: buildDateTime(model.day, model.startTime),
      endsAt: buildDateTime(model.day, model.endTime),
      parentEventId: null,
      workoutExecutionId: null,
      groupSession: null,
      physicalEvaluationSession: null,
      personalTrainingSession: null,
      floorDuty: null,
      vacancy: null,
      recurrenceRules: model.recurrenceRules
        ? {
            frequency: model.recurrenceRules.frequency,
            until: model.recurrenceRules.until,
          }
        : null,
    };

    switch (model.eventableType) {
      case EVENTABLE_TYPE.GroupSession:
        if (
          !model.staffMemberId ||
          !model.disciplineId ||
          !model.roomId ||
          !model.difficultyLevel
        )
          return base;
        return {
          ...base,
          anchor: model.isAnchorEvent ?? false,
          groupSession: {
            staffMemberId: model.staffMemberId,
            disciplineId: model.disciplineId,
            gymRoomId: model.roomId,
            difficultyLevel: model.difficultyLevel,
          },
        };

      case EVENTABLE_TYPE.FloorDuty:
        if (!model.staffMemberId) return base;
        return { ...base, floorDuty: { staffMemberId: model.staffMemberId } };

      case EVENTABLE_TYPE.PersonalTrainingSession:
        if (!model.staffMemberId) return base;
        return {
          ...base,
          personalTrainingSession: { staffMemberId: model.staffMemberId },
        };

      case EVENTABLE_TYPE.PhysicalEvaluationSession:
        if (
          !model.staffMemberId ||
          model.bodyFatPercentage == null ||
          !model.nextEval
        )
          return base;
        return {
          ...base,
          physicalEvaluationSession: {
            staffMemberId: model.staffMemberId,
            bodyFatPercentage: model.bodyFatPercentage,
            nextEval: model.nextEval,
          },
        };

      case EVENTABLE_TYPE.Vacancy:
        return {
          ...base,
          vacancy: { reason: model.reason! },
        };

      default:
        return base;
    }
  }

  // =============================
  // AVAILABLE ATHLETES
  // =============================

  async getAvailableAthletes(
    gymId: number,
    eventId: number,
    search?: string,
    page = 1,
    perPage = 4,
  ): Promise<{
    data: EventAthlete[];
    meta: {
      currentPage: number;
      perPage: number;
      total: number;
      totalPages: number;
    };
  }> {
    const response = await EventsService.instance.getAvailableAthletes(
      gymId,
      eventId,
      search,
      page,
      perPage,
    );

    return {
      data: response.data.athletes,
      meta: {
        currentPage: page,
        perPage,
        total: parseInt(response.headers["x-total-count"] || "0", 10),
        totalPages: parseInt(response.headers["x-total-pages"] || "1", 10),
      },
    };
  }

  // =============================
  // EVENT ATTENDANCES
  // =============================

  async getEventAttendances(
    gymId: number,
    eventId: number,
    search?: string,
  ): Promise<{
    data: EventAttendance[];
    meta: {
      currentPage: number;
      perPage: number;
      total: number;
      totalPages: number;
    };
  }> {
    const response = await EventsService.instance.getEventAttendances(
      gymId,
      eventId,
    );

    let attendances = response.eventAttendances;

    if (search) {
      const normalizedSearch = normalizeForSearch(search);
      attendances = attendances.filter(
        (a) =>
          normalizeForSearch(a.athlete.name).includes(normalizedSearch) ||
          normalizeForSearch(a.athlete.email).includes(normalizedSearch),
      );
    }

    const total = attendances.length;

    return {
      data: attendances,
      meta: { currentPage: 1, perPage: total, totalPages: 1, total },
    };
  }

  // =============================
  // CREATE VACANCY
  // =============================

  async createVacancy(
    gymId: number,
    form: VacancyCreationFormData,
  ): Promise<Event> {
    const payload: EventCreate = {
      title: EVENTABLE_TYPE.Vacancy,
      description: "",
      eventableType: EVENTABLE_TYPE.Vacancy,
      startsAt: form.startsAt,
      endsAt: form.endsAt,
      vacancy: { reason: form.reason },
      recurrenceRules: form.recurrenceRules ?? null,
    };

    return EventsService.instance.createEvent(gymId, payload);
  }

  // =============================
  // UPDATE
  // =============================

  async updateEvent(
    gymId: string,
    id: string,
    form: EventCreationFormData,
    timezone: string,
    updateMode?: UpdateMode,
  ): Promise<EventModel> {
    const payload = this.buildUpdatePayload(form, timezone);

    const event = await EventsService.instance.updateEvent(
      gymId,
      id,
      payload,
      updateMode,
    );

    const staff = extractStaffMember(event);
    const athlete = extractAthlete(event);

    const base: EventModel = {
      id: event.id,
      gymId: event.gym.id,

      title: event.title,
      description: event.description ?? null,
      startsAt: event.startsAt,
      endsAt: event.endsAt,
      recurrenceRules:
        event.recurrenceRules?.frequency && event.recurrenceRules?.until
          ? {
              frequency: event.recurrenceRules.frequency,
              until: event.recurrenceRules.until,
            }
          : null,
      eventableType: event.eventableType,

      recommendedCapacity: event.recommendedCapacity ?? null,
      attendancesCount: event.attendancesCount ?? 0,
      gymScheduleStatus: event.gymSchedule?.status ?? null,
      gymScheduleStartsAt: event.gymSchedule?.startsAt ?? null,
      gymScheduleEndsAt: event.gymSchedule?.endsAt ?? null,

      staffMemberId: staff?.id ?? null,
      staffMemberName: staff?.name ?? null,
      staffMemberAvatarUrl: staff?.avatarUrl ?? null,

      athleteId: athlete?.id ?? null,
      athleteName: athlete?.name ?? null,

      difficultyLevel: null,
      disciplineId: null,
      disciplineName: null,
      disciplineAbbreviation: null,
      disciplineColor: null,

      roomId: null,
      roomName: null,
      roomCapacity: null,

      bodyFatPercentage:
        event.eventableType === EVENTABLE_TYPE.PhysicalEvaluationSession
          ? event.eventable.bodyFatPercentage
          : null,
      nextEval:
        event.eventableType === EVENTABLE_TYPE.PhysicalEvaluationSession
          ? event.eventable.nextEval
          : null,

      reason:
        event.eventableType === EVENTABLE_TYPE.Vacancy
          ? event.eventable.reason
          : null,

      isAnchorEvent: event.anchor,
    };

    if (isGroupSessionDetail(event)) {
      const dr = event.eventable.disciplineRoom;

      const discipline = dr?.discipline ?? null;
      const room = dr?.gymRoom ?? null;

      return {
        ...base,
        difficultyLevel: event.eventable.difficultyLevel ?? null,

        disciplineId: discipline?.id != null ? Number(discipline.id) : null,
        disciplineName: discipline?.name ?? null,
        disciplineAbbreviation: discipline?.abbreviation ?? null,
        disciplineColor: discipline?.color ?? null,

        roomId: room?.id != null ? Number(room.id) : null,
        roomName: room?.name ?? null,
        roomCapacity: room?.maximumCapacity ?? null,

        recommendedCapacity:
          event.recommendedCapacity ?? dr?.recommendedCapacity ?? null,
      };
    }

    return base;
  }
}
